/**
 * AI-Enhanced Retention Actions Hook
 * Sprint 2: Uses Claude to generate personalized retention recommendations
 * based on the specific client's churn risk factors and history.
 * Falls back to templated actions if AI is unavailable.
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ChurnRiskScore } from '@/lib/churn-risk';
import { unwrapFunctionInvoke, logError } from '@/lib/error-utils';

export interface AIRetentionInsight {
  summary: string;
  actions: Array<{
    text: string;
    type: 'call' | 'sms' | 'email' | 'in_person' | 'in_app';
    priority: 'urgent' | 'high' | 'medium';
    reasoning: string;
  }>;
}

/**
 * Hook that generates AI-powered retention insights for at-risk clients.
 * Only triggers for high/critical risk levels to manage API costs.
 */
export function useAIRetentionActions() {
  const [insight, setInsight] = useState<AIRetentionInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateInsight = useCallback(async (
    clientName: string,
    riskScore: ChurnRiskScore,
    clientContext?: {
      totalSessions?: number;
      memberSince?: string;
      lastSessionDate?: string;
      packageSessionsLeft?: number;
    }
  ) => {
    // Only use AI for high/critical risk — template actions are fine for medium/low
    if (riskScore.level !== 'high' && riskScore.level !== 'critical') {
      setInsight(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Build context for the AI
      const riskFactors = Object.entries(riskScore.factors)
        .filter(([, active]) => active)
        .map(([factor]) => factor);

      const prompt = buildRetentionPrompt(clientName, riskScore, riskFactors, clientContext);

      const result = await supabase.functions.invoke('ai-agent', {
        body: {
          message: prompt,
          context: {
            page_title: 'Client Retention',
            current_path: '/retention',
          },
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke<{ message?: string; response?: string }>(result);

      // Parse the AI response into structured actions
      const parsed = parseAIResponse(data?.message || data?.response || '');
      setInsight(parsed);
    } catch (err) {
      // Best-effort enrichment — capture but don't toast; UI falls back to
      // templated retention actions.
      logError(err, 'ai-retention-insight');
      setError('Could not generate AI insights');
      setInsight(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { insight, loading, error, generateInsight };
}

function buildRetentionPrompt(
  clientName: string,
  riskScore: ChurnRiskScore,
  activeFactors: string[],
  context?: {
    totalSessions?: number;
    memberSince?: string;
    lastSessionDate?: string;
    packageSessionsLeft?: number;
  }
): string {
  const factorDescriptions: Record<string, string> = {
    sessionFrequencyDecline: 'their session frequency has dropped significantly',
    missedSessionsIncreasing: 'they\'ve been cancelling or missing more sessions',
    packageEndingSoon: 'their package is running out',
    noRenewalScheduled: 'they haven\'t booked a renewal package',
    longGapSinceLastSession: 'it\'s been a long time since their last session',
    streakBroken: 'their workout streak was broken',
    lowEngagement: 'their overall engagement is low',
    resultsStalling: 'their progress has plateaued',
  };

  const factors = activeFactors
    .map(f => factorDescriptions[f] || f)
    .join(', ');

  let contextStr = '';
  if (context) {
    const parts = [];
    if (context.totalSessions) parts.push(`${context.totalSessions} total sessions`);
    if (context.memberSince) parts.push(`member since ${context.memberSince}`);
    if (context.lastSessionDate) parts.push(`last session: ${context.lastSessionDate}`);
    if (context.packageSessionsLeft != null) parts.push(`${context.packageSessionsLeft} sessions left in package`);
    if (parts.length) contextStr = ` Additional context: ${parts.join(', ')}.`;
  }

  return `I need help retaining a client. ${clientName} has a ${riskScore.level} churn risk (score: ${riskScore.score}/100). The main concerns: ${factors}.${contextStr}

Give me 2-3 specific, actionable things I should do THIS WEEK to keep this client. Be direct and practical — I'm a personal trainer, not a marketer. Format each action as: what to do, how to do it (call/text/email/in-person), and why it will work.`;
}

function parseAIResponse(response: string): AIRetentionInsight {
  // Extract a summary (first paragraph or sentence)
  const lines = response.split('\n').filter(l => l.trim());
  const summary = lines[0]?.replace(/^#+\s*/, '').trim() || 'Retention recommendations generated';

  // Parse numbered actions from the response
  const actions: AIRetentionInsight['actions'] = [];
  const actionRegex = /^\d+[\.\)]\s*\*?\*?(.+)/;

  for (const line of lines) {
    const match = line.match(actionRegex);
    if (match) {
      const text = match[1].replace(/\*\*/g, '').trim();
      // Infer action type from content
      const type = inferActionType(text);
      const priority = actions.length === 0 ? 'urgent' : actions.length === 1 ? 'high' : 'medium';

      actions.push({
        text,
        type,
        priority,
        reasoning: '', // Could parse from subsequent lines
      });
    }
  }

  // If regex didn't find numbered items, treat each paragraph as an action
  if (actions.length === 0 && lines.length > 1) {
    for (const line of lines.slice(1, 4)) {
      const text = line.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '').trim();
      if (text.length > 10) {
        actions.push({
          text,
          type: inferActionType(text),
          priority: actions.length === 0 ? 'urgent' : 'high',
          reasoning: '',
        });
      }
    }
  }

  return { summary, actions };
}

function inferActionType(text: string): 'call' | 'sms' | 'email' | 'in_person' | 'in_app' {
  const lower = text.toLowerCase();
  if (lower.includes('call') || lower.includes('phone')) return 'call';
  if (lower.includes('text') || lower.includes('sms') || lower.includes('message')) return 'sms';
  if (lower.includes('email')) return 'email';
  if (lower.includes('in person') || lower.includes('face to face') || lower.includes('next session')) return 'in_person';
  return 'in_app';
}
